/**
 * One number in an engine's settings: a label, the kit's `Input`, and a hint.
 *
 * The value is edited locally and committed on blur (or Enter), so typing "2"
 * on the way to "20" never becomes the new batch size and sends a save for a
 * value nobody meant. What a commit saves is `commitNumber` in `numberField.ts`.
 *
 * It replaced two private copies, one in each autopilot's settings form, that
 * drew a bare `<input>` inside a `crm-inp` span with a `<label>` wrapped around
 * the lot. The kit's `Input` and `Label` are what the lead watch's form already
 * used, so the three engines' settings now read and behave alike.
 */
import { useEffect, useId, useState } from "react";
import { Input, Label } from "@wa/ui";
import { commitNumber } from "./numberField";

export function EngineNumberField({
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onCommit,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onCommit: (value: number) => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const next = commitNumber(draft, value, min, max);
    setDraft(String(next.value));
    if (next.changed) onCommit(next.value);
  };

  return (
    // `min-w-0`: a grid track sizes itself to its content, and a long hint
    // would otherwise push its track wider than the panel.
    <div className="min-w-0">
      <Label htmlFor={id} className="crm-lab !mt-0 text-[12.5px] font-semibold">
        {label}
      </Label>
      <Input
        id={id}
        value={draft}
        inputMode="numeric"
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className="h-11"
      />
      {hint ? <span className="crm-sub mt-1.5 block text-[11px] leading-snug">{hint}</span> : null}
    </div>
  );
}
